import CustomDocNotFoundError from "@/app/views/errors/CustomDocNotFoundError.vue";
import { loadConfig, Pasta, useNetworkService } from "@pasta/box-v2";
import { PastaAuthBox } from "@pasta/front-auth-v2";
import { PastaInformationBannerBox } from "@pasta/front-information-banner";
import { PastaSentryBox, useSentry } from "@pasta/front-sentry-v2";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { get } from "lodash";
import { createPinia } from "pinia";
import piniaPluginPersistedState from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import { AdminTagsBox } from "./admin-tags/admin-tags.box";
import App from "./app/App.vue";
import "@pasta/box-v2/style.css"; // todo: auto import
import "text-security/text-security.css";
import "./style.css";
import { appErrorManagers } from "./app/error-managers";
import { appRoutes } from "./app/routes";
import { createNetworkRetryInterceptor } from "./app/http/network-retry.interceptor";
import { withNetworkRetryContext, withRouteNameFingerprint } from "./app/utils/sentry";
import CustomForbiddenError from "./app/views/errors/CustomForbiddenError.vue";
import CustomGenericError from "./app/views/errors/CustomGenericError.vue";
import CustomLFNotFoundError from "./app/views/errors/CustomLFNotFoundError.vue";
import CustomNotFoundError from "./app/views/errors/CustomNotFoundError.vue";
import CustomUnavailableError from "./app/views/errors/CustomUnavailableError.vue";
import FluxError from "./app/views/FluxError.vue";
import LinkError from "./app/views/LinkError.vue";
import { VersionAnnouncementBox } from "./version-announcement/version-announcement.box";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

(async function main() {
  await loadConfig(__APP_VERSION__);
  // TODO [develop] fix persisted plugin in pasta-box-v2 not working at the moment
  const pinia = createPinia().use(piniaPluginPersistedState);
  const pasta = new Pasta({
    routerOptions: { routes: [...appRoutes] },
    errorManagers: appErrorManagers,
    pinia,
    errorsMap: {
      GENERIC: CustomGenericError,
      NOT_FOUND: CustomNotFoundError,
      LF_NOT_FOUND: CustomLFNotFoundError,
      DOC_NOT_FOUND: CustomDocNotFoundError,
      UNAVAILABLE: CustomUnavailableError,
      FORBIDDEN: CustomForbiddenError,
      linkerror: LinkError,
      fluxerror: FluxError,
    },
  });

  useNetworkService().addInterceptor(createNetworkRetryInterceptor());

  const sentry = useSentry();
  sentry.getFingerprint = withRouteNameFingerprint(sentry.getFingerprint);
  sentry.getContext = withNetworkRetryContext(sentry.getContext);
  sentry.addBreadcrumbFilter((breadcrumb, _hint?: any) => {
    let url: string = "";
    if (breadcrumb.type === "http") {
      url = get(breadcrumb, "data.url", "");
    }
    if (url && url.includes("dyn/get_challenge")) {
      // ignore scws middleware network errors because port scanning generates too many of them and we can't see other breadcrumbs
      return false;
    }
    return true;
  });

  createApp(App)
    .use(pasta)
    .use(new PastaAuthBox())
    .use(new VersionAnnouncementBox())
    .use(new PastaInformationBannerBox())
    .use(new PastaSentryBox())
    .use(new AdminTagsBox());
  pasta.mount("#app");
})();
